import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
} from "./UI";
import { MaterialLibrarySection } from "./MaterialLibrarySection";
import { LibraryBig, FileText, FileSpreadsheet, ArrowLeft } from "lucide-react";
import { SubscriptionAPI } from "api/dashboard/service";

const libraries = [
  {
    id: "material",
    title: "Material Library",
    description: "Manage organization-level material master",
    icon: LibraryBig,
  },
  {
    id: "quotation",
    title: "Quotation Templates",
    description: "Manage standardized quotation templates",
    icon: FileText,
  },
  {
    id: "boq",
    title: "BOQ Templates",
    description: "Manage bill of quantities templates",
    icon: FileSpreadsheet,
  }
];

const getStoredOrganizationId = () => {
  try {
    return (
      JSON.parse(localStorage.getItem("organization") || "null")?.id || null
    );
  } catch (_) {
    return null;
  }
};

const hasActiveSubscription = (response) =>
  Boolean(
    response?.status &&
    response?.data?.active_flag &&
    response?.data?.valid_till > new Date().toISOString(),
  );

export function OrganizationLibrarySection({ setHideSidebar }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeLibrary, setActiveLibrary] = useState(
    searchParams.get("library") || null,
  );
  const [organizationId, setOrganizationId] = useState(getStoredOrganizationId);
  const [subscriptionActive, setSubscriptionActive] = useState(null);
  const canOpenLibraries = subscriptionActive === true;

  const handleLibraryChange = (libraryId) => {
    if (libraryId && !canOpenLibraries) return;

    setActiveLibrary(libraryId);
    if (libraryId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("section", "organization-library");
        next.set("library", libraryId);
        return next;
      });
    } else {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("library");
        next.set("section", "organization-library");
        return next;
      });
    }
  };

  useEffect(() => {
    const syncOrganization = () => setOrganizationId(getStoredOrganizationId());
    window.addEventListener("storage", syncOrganization);
    window.addEventListener("organizationChanged", syncOrganization);
    return () => {
      window.removeEventListener("storage", syncOrganization);
      window.removeEventListener("organizationChanged", syncOrganization);
    };
  }, []);

  useEffect(() => {
    if (!organizationId) {
      setSubscriptionActive(false);
      return;
    }

    let cancelled = false;
    setSubscriptionActive(null);

    SubscriptionAPI.checkUserSubscription(organizationId)
      .then((response) => {
        if (!cancelled) {
          setSubscriptionActive(hasActiveSubscription(response));
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error checking organization library subscription:", error);
        setSubscriptionActive(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    setActiveLibrary(searchParams.get("library") || null);
  }, [searchParams]);

  useEffect(() => {
    if (subscriptionActive !== false || !activeLibrary) return;

    setActiveLibrary(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("library");
      next.set("section", "organization-library");
      return next;
    });
  }, [activeLibrary, setSearchParams, subscriptionActive]);

  useEffect(() => {
    if (setHideSidebar) {
      setHideSidebar(!!activeLibrary && canOpenLibraries);
    }
    // Cleanup when component unmounts
    return () => {
      if (setHideSidebar) {
        setHideSidebar(false);
      }
    };
  }, [activeLibrary, canOpenLibraries, setHideSidebar]);

  if (canOpenLibraries && activeLibrary === "material") {
    return (
      <div className="space-y-2">
        <Button variant="ghost" className="gap-2 -ml-2 text-text-gray" onClick={() => handleLibraryChange(null)}>
          <ArrowLeft className="h-4 w-4" />
          Back to Libraries
        </Button>
        <MaterialLibrarySection />
      </div>
    );
  }

  // Fallback for other libraries that are not implemented yet
  if (canOpenLibraries && activeLibrary) {
    return (
      <div className="space-y-4 pb-6">
        <Button variant="ghost" className="gap-2 -ml-2 text-text-gray" onClick={() => handleLibraryChange(null)}>
          <ArrowLeft className="h-4 w-4" />
          Back to Libraries
        </Button>
        <div className="flex items-center justify-center p-12 border rounded-lg border-dashed mt-4 bg-gray-50/50">
             <p className="text-text-gray">This library is under construction.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <div>
        <h2 className="text-2xl font-semibold text-primary-text">Organization Library</h2>
        <p className="text-text-gray mt-1">Manage all organization repositories and templates.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {libraries.map((lib) => (
          <Card 
            key={lib.id} 
            aria-disabled={!canOpenLibraries}
            className={`transition-colors border-sm-border/50 shadow-sm ${
              canOpenLibraries
                ? "cursor-pointer hover:border-sm-primary"
                : "cursor-not-allowed opacity-60"
            }`}
            onClick={() => handleLibraryChange(lib.id)}
            title={
              canOpenLibraries ? undefined : "Requires an active subscription"
            }
          >
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-sm-primary/10 flex items-center justify-center mb-3">
                <lib.icon className="h-5 w-5 text-sm-primary" />
              </div>
              <CardTitle className="text-lg">{lib.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-sm">
                {lib.description}
              </CardDescription>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
